import { useEffect, useMemo } from "react";
import L from "leaflet";
import { CircleMarker, MapContainer, Marker, Polygon, Polyline, Popup, TileLayer, useMap, useMapEvents } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { installLeafletDefaultIcon, depotDivIcon, stopNumberIcon } from "@/lib/leaflet-icons";

installLeafletDefaultIcon();

function googleMapsUrl(lat: number, lng: number, subtitle?: string): string {
  const destination = `${lat},${lng}`;
  const qs = new URLSearchParams({ api: "1", destination });
  if (subtitle) qs.set("destination_place", subtitle);
  return `https://www.google.com/maps/dir/?${qs.toString()}`;
}

export interface MapPin {
  id: string;
  lat: number;
  lng: number;
  /** Hover/click popup text. */
  title?: string;
  subtitle?: string;
}

export interface MapDepot extends MapPin {
  label: string;
}

export interface MapStop extends MapPin {
  sequence: number;
  /** Drives the pin variant: "pending" / "arrived" → solid blue with number,
   *  "completed" → green check, "skipped" / "no_answer" → hollow grey ring.
   *  Optional so non-route consumers (e.g. Leads/Map preview pins) don't have
   *  to invent a status. Defaults to pending. */
  status?: "pending" | "arrived" | "completed" | "skipped" | "no_answer";
}

function stopVariantFor(status: MapStop["status"]): "pending" | "done" | "skipped" {
  if (status === "completed") return "done";
  if (status === "skipped" || status === "no_answer") return "skipped";
  return "pending";
}

export interface LeafletMapProps {
  /** Background pins (one color, no labels) — typically all team leads. */
  leads?: MapPin[];
  /** Highlighted depots with name labels. */
  depots?: MapDepot[];
  /** Numbered ordered stops for an active route. */
  stops?: MapStop[];
  /** Polyline drawn through depot → stops → depot. */
  polyline?: Array<[number, number]>;
  /** Saved zone polygons to overlay (rendered semi-transparent in their own
   *  color). Used by Leads/Map to show existing zones in context. */
  zonePolygons?: Array<{ id: string; positions: Array<[number, number]>; color: string }>;
  /** In-progress drawing polygon — rendered as a dashed outline so it's
   *  visually distinct from saved zones. Each click on the map appends a
   *  vertex via onMapClick. */
  drawingPolygon?: Array<[number, number]>;
  /** Fires when the user single-clicks the map (only if provided). Pass a
   *  handler to enable click-to-draw mode; omit to disable map clicking. */
  onMapClick?: (latlng: [number, number]) => void;
  className?: string;
  height?: number;
  /** If true, auto-fit bounds whenever pins change. Defaults true. */
  autoFit?: boolean;
}

// Centroid of the contiguous US — used only as a last-ditch fallback when we
// have zero usable points to fit to. Driving routes always have at least a
// depot, so this is mostly cosmetic for the empty initial load.
const DEFAULT_CENTER: [number, number] = [39.8283, -98.5795];
const DEFAULT_ZOOM = 5;
// Stop zoom-outs from collapsing the visible area into a repeated world tile;
// driving routes never need to see Antarctica.
const MIN_ZOOM = 4;
const SINGLE_POINT_ZOOM = 13;
const FIT_MAX_ZOOM = 14;
const FIT_PADDING: [number, number] = [40, 40];

/**
 * Junk filter for lat/lng pairs. Geocoder failures often write (0, 0) into the
 * lead row instead of nulling them out, and a single (0, 0) point in the fit
 * set is enough to stretch the bounds from Ohio to the Gulf of Guinea — which
 * is why the map was opening on Africa. Drop those before fitting.
 */
function isUsableCoord(lat: number, lng: number): boolean {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
  if (lat === 0 && lng === 0) return false;
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return false;
  return true;
}

/**
 * Pick points the map should *frame*, in priority order:
 *   1. Stops + depots — the actual route. Always wins if any exist.
 *   2. Background lead pins — used only when the route is empty.
 * This means a 20-stop selection in Ohio always frames Ohio, regardless of how
 * many far-flung or junk-geocoded background pins are on screen.
 */
function pickFitPoints(
  depots: MapDepot[],
  stops: MapStop[],
  leads: MapPin[],
): Array<[number, number]> {
  const primary: Array<[number, number]> = [];
  for (const p of depots) if (isUsableCoord(p.lat, p.lng)) primary.push([p.lat, p.lng]);
  for (const p of stops) if (isUsableCoord(p.lat, p.lng)) primary.push([p.lat, p.lng]);
  if (primary.length > 0) return primary;
  const fallback: Array<[number, number]> = [];
  for (const p of leads) if (isUsableCoord(p.lat, p.lng)) fallback.push([p.lat, p.lng]);
  return fallback;
}

function FitBounds({ points }: { points: Array<[number, number]> }) {
  const map = useMap();
  useEffect(() => {
    if (!points.length) return;
    if (points.length === 1) {
      map.setView(points[0], SINGLE_POINT_ZOOM);
      return;
    }
    const bounds = L.latLngBounds(points.map(([lat, lng]) => L.latLng(lat, lng)));
    map.fitBounds(bounds, { padding: FIT_PADDING, maxZoom: FIT_MAX_ZOOM });
  }, [map, points]);
  return null;
}

/**
 * Forwards single map clicks to the parent. Mounted only when onMapClick is
 * set — when there's no draw handler, the map's normal pan/zoom behaviour
 * stays untouched.
 */
function MapClickRelay({ onMapClick }: { onMapClick: (latlng: [number, number]) => void }) {
  useMapEvents({
    click(e) {
      onMapClick([e.latlng.lat, e.latlng.lng]);
    },
  });
  return null;
}

export function LeafletMap({
  leads = [],
  depots = [],
  stops = [],
  polyline,
  zonePolygons = [],
  drawingPolygon,
  onMapClick,
  className,
  height = 600,
  autoFit = true,
}: LeafletMapProps) {
  const fitPoints: Array<[number, number]> = useMemo(
    () => pickFitPoints(depots, stops, leads),
    [leads, depots, stops],
  );

  // Canvas renderer for the background lead CircleMarkers. With 5,000+ leads
  // the default SVG renderer creates 5,000+ DOM nodes; canvas paints them
  // all in a single layer with ~10× the throughput on mid-range devices and
  // ~20× on mobile. Stops + depots use HTML divIcons (separate render path
  // entirely) so they're unaffected. Polylines + zone polygons keep their
  // default SVG renderer because they need crisp hover + click hit-testing.
  const leadCanvasRenderer = useMemo(() => L.canvas({ padding: 0.5 }), []);

  // Compute initial bounds/center from the first render's fit points so the
  // map opens already framed on the route — avoids the flash of US-centroid
  // view that FitBounds would otherwise correct on the next tick.
  const initialView = useMemo(() => {
    if (fitPoints.length === 0) {
      return { center: DEFAULT_CENTER, zoom: DEFAULT_ZOOM, bounds: undefined as L.LatLngBounds | undefined };
    }
    if (fitPoints.length === 1) {
      return { center: fitPoints[0], zoom: SINGLE_POINT_ZOOM, bounds: undefined };
    }
    return {
      center: undefined as [number, number] | undefined,
      zoom: undefined as number | undefined,
      bounds: L.latLngBounds(fitPoints.map(([lat, lng]) => L.latLng(lat, lng))),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentional: only the FIRST render's points seed the initial view; FitBounds handles updates

  return (
    <div className={className} style={{ height }}>
      <MapContainer
        center={initialView.center}
        zoom={initialView.zoom}
        bounds={initialView.bounds}
        boundsOptions={{ padding: FIT_PADDING, maxZoom: FIT_MAX_ZOOM }}
        minZoom={MIN_ZOOM}
        worldCopyJump={false}
        scrollWheelZoom
        style={{ height: "100%", width: "100%", borderRadius: 8 }}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://osm.org/copyright">OpenStreetMap</a>'
          noWrap
        />

        {leads.filter((p) => isUsableCoord(p.lat, p.lng)).map((p) => (
          <CircleMarker
            key={`lead-${p.id}`}
            center={[p.lat, p.lng]}
            radius={4}
            renderer={leadCanvasRenderer}
            pathOptions={{ color: "#475569", fillColor: "#94a3b8", fillOpacity: 0.85, weight: 1 }}
          >
            {(p.title || p.subtitle) && (
              <Popup>
                <div className="text-xs">
                  {p.title && <div className="font-semibold text-slate-900">{p.title}</div>}
                  {p.subtitle && <div className="text-slate-600">{p.subtitle}</div>}
                  <a
                    href={googleMapsUrl(p.lat, p.lng, p.subtitle)}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-1 inline-block font-medium text-sky-700 hover:underline"
                  >
                    Open in Google Maps
                  </a>
                </div>
              </Popup>
            )}
          </CircleMarker>
        ))}

        {depots.filter((d) => isUsableCoord(d.lat, d.lng)).map((d) => (
          <Marker key={`depot-${d.id}`} position={[d.lat, d.lng]} icon={depotDivIcon(d.label)}>
            {(d.title || d.subtitle) && (
              <Popup>
                <div className="text-xs">
                  {d.title && <div className="font-semibold text-slate-900">{d.title}</div>}
                  {d.subtitle && <div className="text-slate-600">{d.subtitle}</div>}
                  <a
                    href={googleMapsUrl(d.lat, d.lng, d.subtitle)}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-1 inline-block font-medium text-sky-700 hover:underline"
                  >
                    Open in Google Maps
                  </a>
                </div>
              </Popup>
            )}
          </Marker>
        ))}

        {stops.filter((s) => isUsableCoord(s.lat, s.lng)).map((s) => (
          <Marker key={`stop-${s.id}`} position={[s.lat, s.lng]} icon={stopNumberIcon(s.sequence, stopVariantFor(s.status))}>
            {(s.title || s.subtitle) && (
              <Popup>
                <div className="text-xs">
                  {s.title && <div className="font-semibold text-slate-900">Stop {s.sequence} — {s.title}</div>}
                  {s.subtitle && <div className="text-slate-600">{s.subtitle}</div>}
                  <a
                    href={googleMapsUrl(s.lat, s.lng, s.subtitle)}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-1 inline-block font-medium text-sky-700 hover:underline"
                  >
                    Open in Google Maps
                  </a>
                </div>
              </Popup>
            )}
          </Marker>
        ))}

        {polyline && polyline.length > 1 && (
          <Polyline positions={polyline} pathOptions={{ color: "#0284c7", weight: 4, opacity: 0.7 }} />
        )}

        {zonePolygons.map((z) => (
          <Polygon
            key={`zone-${z.id}`}
            positions={z.positions}
            pathOptions={{ color: z.color, fillColor: z.color, fillOpacity: 0.12, weight: 2, opacity: 0.85 }}
          />
        ))}

        {drawingPolygon && drawingPolygon.length > 0 && (
          <>
            {drawingPolygon.length >= 3 ? (
              <Polygon
                positions={drawingPolygon}
                pathOptions={{ color: "#dc2626", fillColor: "#dc2626", fillOpacity: 0.08, weight: 2, dashArray: "6 6" }}
              />
            ) : (
              <Polyline
                positions={drawingPolygon}
                pathOptions={{ color: "#dc2626", weight: 2, dashArray: "6 6" }}
              />
            )}
            {drawingPolygon.map(([lat, lng], i) => (
              <CircleMarker
                key={`draw-vertex-${i}`}
                center={[lat, lng]}
                radius={4}
                pathOptions={{ color: "#dc2626", fillColor: "#fff", fillOpacity: 1, weight: 2 }}
              />
            ))}
          </>
        )}

        {onMapClick && <MapClickRelay onMapClick={onMapClick} />}

        {autoFit && fitPoints.length > 0 && <FitBounds points={fitPoints} />}
      </MapContainer>
    </div>
  );
}
